import React, { useContext, useState } from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';

import ImageView from 'lib/images/image-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import SVGPureIcon from 'lib/images/svg-pure-icon/svg-pure-icon';

import webIcon from 'four-nets/icons/svg/icon_website.svg';
import emailIcon from 'four-nets/icons/svg/icon_email.svg';
import phoneIcon from 'four-nets/icons/svg/icon_phone.svg';
import placeIcon from 'four-nets/icons/svg/icon_place.svg';
import facebookIcon from 'four-nets/icons/svg/icon_social-facebook.svg';
import instagramIcon from 'four-nets/icons/svg/icon_social-instagram.svg';
import photoIcon from 'four-nets/icons/svg/icon_photo.svg';
import OutsideLink from 'four-nets/ui-v2/outside-link/outside-link';
import { ViewerContext } from 'four-nets/user/viewer/viewer';

import AuthorSVGIcon from '../author-svg-icon/author-svg-icon';
import mutation, { UpdateVendorLogoMutation, UpdateVendorLogoMutationVariables } from './update-vendor-logo.graphql';
import { VendorInformationFragment } from './vendor-information.graphql';
import defaultStyles from './vendor-information.scss';
import { AlertContext } from 'lib/alert-context/alert-context';

export interface Props {
    styles: typeof defaultStyles;
    vendor?: VendorInformationFragment;
}

const ensureUrlIsAbsolute = (url: string) => {
    if (url.match(/^(?:[a-z]+:)?\/\//)) {
        return url;
    }
    return `//${url}`;
};

const VendorInformation: React.FC<Props> = props => {
    const { styles, vendor } = props;
    const { viewer } = useContext(ViewerContext);
    const [key, setKey] = useState(0);
    const [updateLogoFn] = useMutation<UpdateVendorLogoMutation, UpdateVendorLogoMutationVariables>(mutation);

    const { acknowledge, confirm } = useContext(AlertContext);

    if (vendor == null) {
        return null;
    }

    const { web, user, phone, address, logo, facebookPageUrl, instagramPageUrl, cardText } = vendor;
    const email = vendor?.customer?.email;
    const isAuthor = (viewer && viewer.username) === (user && user.username) || (viewer && viewer.isSuperstaff);

    return (
        <React.Fragment>
            <div className={styles.cardText}>{cardText}</div>
            {web || isAuthor ? (
                <div className={styles.vendorInfoItem}>
                    <AuthorSVGIcon src={webIcon} />
                    <div className={styles.vendorLinkDiv}>
                        {web ? <OutsideLink style={{ link: styles.outsideLinkSpan }} link={web} /> : null}
                    </div>
                </div>
            ) : null}
            {email || isAuthor ? (
                <div className={styles.vendorInfoItem}>
                    <AuthorSVGIcon src={emailIcon} />
                    <div className={styles.vendorLinkDiv}>
                        {email ? <OutsideLink style={{ link: styles.outsideLinkSpan }} link={email} /> : null}
                    </div>
                </div>
            ) : null}
            {phone || isAuthor ? (
                <div className={styles.vendorInfoItem}>
                    <AuthorSVGIcon src={phoneIcon} />
                    {phone}
                </div>
            ) : null}
            {address || isAuthor ? (
                <div className={styles.vendorInfoItem}>
                    <AuthorSVGIcon src={placeIcon} />
                    {address}
                </div>
            ) : null}
            {logo || isAuthor ? (
                <>
                    <div
                        className={classnames(styles.vendorLogo, {
                            [styles.noLogo]: !Boolean(logo),
                        })}
                    >
                        {logo ? (
                            <ImageView
                                style={{ img: styles.img }}
                                sizes={{ xs: 310 }}
                                height={90}
                                aspectFill={true}
                                src={logo.src}
                                srcSet={logo.srcset || undefined}
                            />
                        ) : (
                            <SVGPureIcon src={photoIcon} className={styles.noLogoIcon} />
                        )}
                    </div>
                    <>
                        {isAuthor ? (
                            <div className={styles.logoButtons}>
                                <div className={styles.updateLogo}>
                                    <FormattedMessage id='CHANGE_LOGO' />
                                    <input
                                        type='file'
                                        key={key}
                                        className={styles.input}
                                        multiple={false}
                                        accept='image/x-png,image/bmp,image/jpeg'
                                        onChange={event => {
                                            if (event.target.files && event.target.files.length) {
                                                updateLogoFn({
                                                    variables: { input: { imageFile: event.target.files[0] } },
                                                })
                                                    .then(() => {
                                                        setKey(key + 1);
                                                    })
                                                    .catch((err: Error) => {
                                                        acknowledge!({ body: err.message });
                                                    });
                                            }
                                        }}
                                    />
                                </div>
                                {logo ? (
                                    <button
                                        className={styles.removeLogo}
                                        onClick={() => {
                                            const onAccept = () => {
                                                updateLogoFn({ variables: { input: { imageFile: null } } }).catch(
                                                    (err: Error) => {
                                                        acknowledge!({ body: err.message });
                                                    }
                                                );
                                            };
                                            confirm!({
                                                body: <FormattedMessage id='ARE_YOU_SURE_VENDOR' />,
                                                onAccept,
                                            });
                                        }}
                                    >
                                        <FormattedMessage id='DELETE_LOGO' values={{ gender: 'v' }} />
                                    </button>
                                ) : null}
                            </div>
                        ) : null}
                    </>
                </>
            ) : null}
            <div className={styles.socialMediaDiv}>
                {facebookPageUrl || isAuthor ? (
                    <a
                        href={ensureUrlIsAbsolute(facebookPageUrl || '')}
                        className={styles.socialMediaLink}
                        target='_blank'
                        rel='noopener'
                    >
                        <SVGIcon style={{ image: styles.icon }} src={facebookIcon} className={styles.facebook} />
                        <FormattedMessage id='FACEBOOK' />
                    </a>
                ) : null}
                {instagramPageUrl || isAuthor ? (
                    <a
                        href={ensureUrlIsAbsolute(instagramPageUrl || '')}
                        className={styles.socialMediaLink}
                        target='_blank'
                        rel='noopener'
                    >
                        <SVGIcon style={{ image: styles.icon }} src={instagramIcon} className={styles.instagram} />
                        <FormattedMessage id='INSTAGRAM' />
                    </a>
                ) : null}
            </div>
        </React.Fragment>
    );
};

export default Styled(defaultStyles)(VendorInformation);
